import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import TableActionButton from "@components/buttons/TableActionButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { ipBlocklistService } from "@services/apis/IpBlocklist";
import { IpBlocklistEntry } from "@services/models/IpManagement/IpBlocklistModel";
import { formatDisplayDate } from "@utils/safeDate";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import AddIpBlocklistSlider from "../../ip-blocklist/components/AddIpBlocklistSlider";

const PAGE_LIMIT = 50;

const MobileIpBlocklist = () => {
  const gridRef = useRef<any>(null);
  const { handleError, handleSuccess } = useSnackbarMessage();
  const inFlight = useRef(false);

  const [rows, setRows] = useState<IpBlocklistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [removingIp, setRemovingIp] = useState<string | null>(null);
  const [addSliderOpen, setAddSliderOpen] = useState(false);

  const fetchBlockedIps = useCallback(
    async (pageNum: number, reset: boolean = false) => {
      if (inFlight.current) return;
      inFlight.current = true;

      try {
        setLoading(true);
        setError("");

        const res = await ipBlocklistService.listBlockedIps({
          limit: PAGE_LIMIT,
          offset: (pageNum - 1) * PAGE_LIMIT,
        });

        if (res.success) {
          const fetched = res.data || [];
          setHasMore(fetched.length === PAGE_LIMIT);
          if (reset) {
            setRows(fetched);
          } else {
            setRows((prev) => [...prev, ...fetched]);
          }
        } else {
          if (reset) setRows([]);
          setHasMore(false);
          setError(res.msg || "Failed to fetch blocked IPs");
        }
      } catch {
        if (reset) setRows([]);
        setHasMore(false);
        setError("Failed to fetch blocked IPs");
      } finally {
        setLoading(false);
        inFlight.current = false;
      }
    },
    []
  );

  const refreshList = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchBlockedIps(1, true);
  }, [fetchBlockedIps]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (page > 1 && hasMore) {
      fetchBlockedIps(page, false);
    }
  }, [page, hasMore, fetchBlockedIps]);

  const handleRemoveIp = useCallback(
    async (ipAddress: string) => {
      setRemovingIp(ipAddress);
      try {
        const response = await ipBlocklistService.removeIp({ ip: ipAddress });
        handleSuccess({
          message: response.msg || "IP removed from blocklist successfully",
        });
        refreshList();
      } catch (err: any) {
        handleError({
          message:
            err?.response?.data?.msg ||
            "Failed to remove IP from blocklist",
        });
      } finally {
        setRemovingIp(null);
      }
    },
    [handleError, handleSuccess, refreshList]
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "IP Address",
        field: "ip_address",
        flex: 1,
        minWidth: 140,
        cellRenderer: ({ data }: any) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span>{data.ip_address}</span>
            <CopyButton
              copyText={data.ip_address}
              copyMessage="IP copied!"
              copyIconSize={10}
            />
          </div>
        ),
      },
      {
        headerName: "Reason",
        field: "reason",
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) => params.value || "—",
      },
      {
        headerName: "Blocked By",
        field: "blocked_by",
        minWidth: 100,
      },
      {
        headerName: "Blocked At",
        field: "created_at",
        minWidth: 150,
        valueFormatter: (params) => formatDisplayDate(params.value),
      },
      {
        headerName: "Action",
        field: "action",
        minWidth: 130,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: any) => {
          const isRemoving = removingIp === data.ip_address;
          return (
            <TableActionButton
              variant="danger"
              icon="link_off"
              loading={isRemoving}
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveIp(data.ip_address);
              }}
            >
              Remove
            </TableActionButton>
          );
        },
      },
    ],
    [handleRemoveIp, removingIp]
  );

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (row) =>
        row.ip_address.toLowerCase().includes(term) ||
        (row.reason?.toLowerCase().includes(term) ?? false) ||
        row.blocked_by.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading || searchTerm) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, loading, searchTerm]
  );

  return (
    <div className="flex flex-col h-full">
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex items-center justify-between px-4 sticky top-0 z-10 gap-2">
        <p className="text-text-base text-[14px] font-semibold">
          IP Blocklist
        </p>
        <LightCustomButton
          text="BLOCK IP"
          onClick={() => setAddSliderOpen(true)}
          isActive={true}
          className="min-w-[90px]"
        />
      </div>

      <div className="px-3 pt-3">
        <GlobalTextInputField
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search IP / Reason / Admin ID"
          prefixIcon={
            <span className="material-symbols-outlined text-text-subtle">
              search
            </span>
          }
        />
      </div>

      <div
        className="bg-bg-page-body p-3 flex-1 overflow-x-auto"
        style={{ height: "calc(100dvh - 120px)" }}
      >
        {loading && page === 1 ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <div className="flex flex-col h-full">
            <div className="ag-theme-alpine ag-grid-12px flex-1 min-h-0">
              <AgGridReactTable
                ref={gridRef}
                rowData={filteredRows}
                columnDefs={columnDefs}
                onBodyScroll={onBodyScroll}
                error={error}
                getRowId={(params) => params.data.id.toString()}
              />
            </div>
            {loading && rows.length > 0 && (
              <div className="flex items-center justify-center py-3 border-t border-border-subtle">
                <div className="flex items-center space-x-2 text-text-subtle">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary"></div>
                  <span className="text-xs">Loading more IPs...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AddIpBlocklistSlider
        isOpen={addSliderOpen}
        onClose={() => setAddSliderOpen(false)}
        onSuccess={refreshList}
      />
    </div>
  );
};

export default MobileIpBlocklist;
